/**
 * Modern listing icon system for HALO NeighborhoodModule
 *
 * Reuses existing PNG assets from gmapIcons.ts but provides a cleaner API
 * and memoization for HALO components.
 */

// Import existing PNG assets
import buildingGray from 'images/hp-icons/building-gray.png';
import buildingTeal from 'images/hp-icons/building-teal.png';
import buildingRed from 'images/hp-icons/building-red.png';
import buildingYellow from 'images/hp-icons/building-yellow.png';
import houseGray from 'images/hp-icons/house-gray.png';
import houseTeal from 'images/hp-icons/house-teal.png';
import houseRed from 'images/hp-icons/house-red.png';
import houseYellow from 'images/hp-icons/house-yellow.png';
import roomGray from 'images/hp-icons/room-gray.png';
import roomTeal from 'images/hp-icons/room-teal.png';
import roomRed from 'images/hp-icons/room-red.png';
import roomYellow from 'images/hp-icons/room-yellow.png';

export type ListingIconColor = 'red' | 'gray' | 'yellow' | 'teal';
export type ListingIconType = 'building' | 'house' | 'room';

// Icon configuration matching existing gmapIcons.ts patterns
const ICON_CONFIGS = {
  building: {
    size: { width: 22, height: 36 },
    anchor: { x: 11, y: 18 }, // Center anchor
    assets: {
      red: buildingRed,
      gray: buildingGray,
      yellow: buildingYellow,
      teal: buildingTeal,
    },
  },
  house: {
    size: { width: 22, height: 26 },
    anchor: { x: 11, y: 13 }, // Center anchor
    assets: {
      red: houseRed,
      gray: houseGray,
      yellow: houseYellow,
      teal: houseTeal,
    },
  },
  room: {
    size: { width: 31, height: 25 },
    anchor: { x: 15, y: 12 }, // Center anchor
    assets: {
      red: roomRed,
      gray: roomGray,
      yellow: roomYellow,
      teal: roomTeal,
    },
  },
} as const;

/**
 * Creates a Google Maps marker icon for listings using existing PNG assets
 * @param type - Type of listing icon (building, house, room)
 * @param color - Color variant for different states
 * @param size - Marker size in pixels (responsive 3-12px range)
 * @returns Google Maps Icon configuration
 */
export const createListingMarkerIcon = (
  type: ListingIconType,
  color: ListingIconColor,
  size: number,
): google.maps.Icon => {
  const config = ICON_CONFIGS[type];
  const iconUrl = config.assets[color];

  // Calculate scaled size while maintaining aspect ratio
  const aspectRatio = config.size.width / config.size.height;
  const scaledWidth = size;
  const scaledHeight = size / aspectRatio;

  return {
    url: iconUrl,
    scaledSize: new window.google.maps.Size(scaledWidth, scaledHeight),
    anchor: new window.google.maps.Point(scaledWidth / 2, scaledHeight / 2),
  };
};

/**
 * Icon type selection based on listing properties
 * Replicates logic from gmapIcons.ts iconForListing function
 */
export const getListingIconType = (listing: {
  propertyType?: string;
  unitType?: string;
  buildingType?: string;
  listingType?: string;
  iconType?: string;
}): ListingIconType => {
  // Use existing iconType if provided (from gmapIcons.ts compatibility)
  if (listing.iconType && ['building', 'house', 'room'].includes(listing.iconType)) {
    return listing.iconType as ListingIconType;
  }

  // Room rentals (matches gmapIcons.ts logic)
  if (listing.listingType === 'room') {
    return 'room';
  }

  // Check for room-related keywords
  if (listing.unitType?.toLowerCase().includes('room') || listing.propertyType?.toLowerCase().includes('room')) {
    return 'room';
  }

  // Single-family homes, townhomes, condos
  if (
    listing.propertyType?.toLowerCase().includes('house') ||
    listing.propertyType?.toLowerCase().includes('townhome') ||
    listing.propertyType?.toLowerCase().includes('condo') ||
    listing.buildingType?.toLowerCase().includes('single')
  ) {
    return 'house';
  }

  // Apartments, multi-unit buildings (default)
  return 'building';
};

/**
 * Color selection based on listing state
 * Replicates logic from gmapIcons.ts iconForListing function
 */
export const getListingIconColor = (
  listing: {
    id?: string;
    isFavorited?: boolean;
    isActive?: boolean;
    userItemTypes?: string[];
  },
  viewedListingIds: Set<string> = new Set(),
): ListingIconColor => {
  // Check userItemTypes array (matches gmapIcons.ts logic)
  if (listing.userItemTypes?.includes('favorite') || listing.isFavorited) {
    return 'yellow';
  }

  // Active/selected state (isActive parameter in gmapIcons.ts)
  if (listing.isActive) {
    return 'teal';
  }

  // Previously viewed state
  if (listing.userItemTypes?.includes('viewed') || (listing.id && viewedListingIds.has(listing.id))) {
    return 'gray';
  }

  // Default state
  return 'red';
};

// Memoization cache for performance optimization
const iconCache = new Map<string, google.maps.Icon>();

/**
 * Memoized version of createListingMarkerIcon for performance
 * Caches generated icons to prevent regeneration of identical markers
 */
export const createCachedListingMarkerIcon = (
  type: ListingIconType,
  color: ListingIconColor,
  size: number,
): google.maps.Icon => {
  const cacheKey = `${type}-${color}-${size}`;

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!;
  }

  const icon = createListingMarkerIcon(type, color, size);
  iconCache.set(cacheKey, icon);

  return icon;
};

/**
 * Clear the icon cache (useful for memory management)
 */
export const clearListingIconCache = (): void => {
  iconCache.clear();
};
